import { CollapsibleFilters } from '../../components/common/CollapsibleFilters';
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  ModalClose,
  Stack,
  Typography,
  FormControl,
  FormLabel,
} from '@mui/joy';
import { Plus as AddIcon, PencilSimple as EditIcon, Trash as DeleteIcon, Tag as LocalOfferIcon, Warning as WarningIcon } from '@phosphor-icons/react';
import type { Tag } from '../../types';
import { PageHeader } from '../../components/common/PageHeader';
import { ResponsiveTable } from '../../components/common/ResponsiveTable';

// Preset color palette
const PRESET_COLORS = [
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Green', value: '#10b981' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Lime', value: '#84cc16' },
];

interface TagManagementPageProps {
  // Props would normally come from hooks, but showing the structure here
  tags?: Tag[];
  onCreateTag?: (name: string, color: string) => Promise<void>;
  onUpdateTag?: (id: number, name: string, color: string) => Promise<void>;
  onDeleteTag?: (id: number) => Promise<void>;
}

/**
 * TagManagementPage - Settings page for managing organization tags
 *
 * Features:
 * - View all tags with usage counts
 * - Create new tags
 * - Edit tag name and color
 * - Soft delete tags (with confirmation if in use)
 * - Search/filter tags
 */
export const TagManagementPage: React.FC<TagManagementPageProps> = ({
  tags = [],
  onCreateTag,
  onUpdateTag,
  onDeleteTag,
}) => {
  const [actionError, setActionError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
  const [newTagName, setNewTagName] = useState('');
  const [selectedColor, setSelectedColor] = useState(PRESET_COLORS[0].value);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter tags by search query
  const filteredTags = tags.filter(
    (tag) =>
      !tag.is_deleted &&
      tag.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateTag = async () => {
    if (!newTagName.trim() || !onCreateTag) return;

    setActionError('');
    setIsSubmitting(true);
    try {
      await onCreateTag(newTagName.trim(), selectedColor);
      setShowCreateModal(false);
      setNewTagName('');
      setSelectedColor(PRESET_COLORS[0].value);
    } catch (error) {
      setActionError('Could not create this tag. Please try again.');
      console.error('Failed to create tag:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditTag = async () => {
    if (!selectedTag || !newTagName.trim() || !onUpdateTag) return;

    setActionError('');
    setIsSubmitting(true);
    try {
      await onUpdateTag(selectedTag.id, newTagName.trim(), selectedColor);
      setShowEditModal(false);
      setSelectedTag(null);
      setNewTagName('');
      setSelectedColor(PRESET_COLORS[0].value);
    } catch (error) {
      setActionError('Could not update this tag. Please try again.');
      console.error('Failed to update tag:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTag = async () => {
    if (!selectedTag || !onDeleteTag) return;

    setActionError('');
    setIsSubmitting(true);
    try {
      await onDeleteTag(selectedTag.id);
      setShowDeleteModal(false);
      setSelectedTag(null);
    } catch (error) {
      setActionError('Could not delete this tag. Please try again.');
      console.error('Failed to delete tag:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (tag: Tag) => {
    setActionError('');
    setSelectedTag(tag);
    setNewTagName(tag.name);
    setSelectedColor(tag.color);
    setShowEditModal(true);
  };

  const openDeleteModal = (tag: Tag) => {
    setActionError('');
    setSelectedTag(tag);
    setShowDeleteModal(true);
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader title="Tags" description="Create reusable labels to organize contacts, jobs, opportunities, and photos." />
      {/* Actions Bar */}
      <Button
          startDecorator={<AddIcon />}
          onClick={() => { setActionError(''); setShowCreateModal(true); }}
        >
          Create Tag
        </Button>
<CollapsibleFilters><Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <Input
          placeholder="Search tags..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flexGrow: 1, maxWidth: { xs: 'none', sm: 400 } }}
        />

      </Box></CollapsibleFilters>

      <Card sx={{ p: 0, overflow: 'hidden' }}>
        <ResponsiveTable rows={filteredTags} getRowKey={tag => tag.id} columns={[
          { key: 'name', header: 'Tag', mobileVariant: 'header', render: tag => <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><LocalOfferIcon color={tag.color} size={20} /><Typography level="title-sm" sx={{ overflowWrap: 'anywhere' }}>{tag.name}</Typography></Box> },
          { key: 'color', header: 'Color', render: tag => <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box aria-hidden="true" sx={{ width: 14, height: 14, bgcolor: tag.color, borderRadius: '50%', border: '1px solid', borderColor: 'divider' }} /><Typography level="body-sm">{tag.color}</Typography></Box> },
          { key: 'usage', header: 'Usage', render: tag => `${tag.usage_count || 0} items` },
        ]} rowActions={tag => <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton aria-label={`Edit ${tag.name}`} size="sm" variant="plain" onClick={() => openEditModal(tag)}><EditIcon /></IconButton>
          <IconButton aria-label={`Delete ${tag.name}`} size="sm" color="danger" variant="plain" onClick={() => openDeleteModal(tag)}><DeleteIcon /></IconButton>
        </Box>} emptyState={<Box sx={{ p: 3, textAlign: 'center' }}><Typography>{searchQuery ? 'No tags match your search.' : 'No tags yet. Create a tag to organize your records.'}</Typography>{searchQuery && <Button variant="plain" onClick={() => setSearchQuery('')}>Clear search</Button>}</Box>} />
      </Card>

      {/* Create Tag Modal */}
      <Modal open={showCreateModal} onClose={() => setShowCreateModal(false)}>
        <ModalDialog sx={{ maxWidth: { xs: 'calc(100vw - 16px)', sm: 'none' }, maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          {actionError && <Alert color="danger">{actionError}</Alert>}
          <Typography level="h4" sx={{ mb: 2 }}>
            Create New Tag
          </Typography>

          <Stack spacing={2}>
            <FormControl>
              <FormLabel>Tag Name</FormLabel>
              <Input
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="Enter tag name"
                autoFocus
              />
            </FormControl>

            <FormControl>
              <FormLabel>Color</FormLabel>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {PRESET_COLORS.map((color) => (
                  <Box
                    key={color.value}
                    component="button" type="button" aria-label={`${color.name} tag color`} aria-pressed={selectedColor === color.value}
                    onClick={() => setSelectedColor(color.value)}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '8px',
                      backgroundColor: color.value,
                      cursor: 'pointer',
                      border: selectedColor === color.value ? '3px solid' : '2px solid',
                      borderColor:
                        selectedColor === color.value ? 'primary.500' : 'neutral.300',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'scale(1.1)',
                      },
                    }}
                    title={color.name}
                  />
                ))}
              </Box>
            </FormControl>

            <Box>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                Preview:
              </Typography>
              <Chip
                size="md"
                variant="solid"
                startDecorator={<LocalOfferIcon />}
                sx={{
                  backgroundColor: selectedColor,
                  color: '#fff',
                }}
              >
                {newTagName || 'Tag Name'}
              </Chip>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="plain"
                color="neutral"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateTag}
                loading={isSubmitting}
                disabled={!newTagName.trim()}
              >
                Create Tag
              </Button>
            </Box>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Edit Tag Modal */}
      <Modal open={showEditModal} onClose={() => setShowEditModal(false)}>
        <ModalDialog sx={{ maxWidth: { xs: 'calc(100vw - 16px)', sm: 'none' }, maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          {actionError && <Alert color="danger">{actionError}</Alert>}
          <Typography level="h4" sx={{ mb: 2 }}>
            Edit Tag
          </Typography>

          <Stack spacing={2}>
            <FormControl>
              <FormLabel>Tag Name</FormLabel>
              <Input
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="Enter tag name"
                autoFocus
              />
            </FormControl>

            <FormControl>
              <FormLabel>Color</FormLabel>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {PRESET_COLORS.map((color) => (
                  <Box
                    key={color.value}
                    component="button" type="button" aria-label={`${color.name} tag color`} aria-pressed={selectedColor === color.value}
                    onClick={() => setSelectedColor(color.value)}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '8px',
                      backgroundColor: color.value,
                      cursor: 'pointer',
                      border: selectedColor === color.value ? '3px solid' : '2px solid',
                      borderColor:
                        selectedColor === color.value ? 'primary.500' : 'neutral.300',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'scale(1.1)',
                      },
                    }}
                    title={color.name}
                  />
                ))}
              </Box>
            </FormControl>

            <Box>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                Preview:
              </Typography>
              <Chip
                size="md"
                variant="solid"
                startDecorator={<LocalOfferIcon />}
                sx={{
                  backgroundColor: selectedColor,
                  color: '#fff',
                }}
              >
                {newTagName || 'Tag Name'}
              </Chip>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="plain"
                color="neutral"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleEditTag}
                loading={isSubmitting}
                disabled={!newTagName.trim()}
              >
                Save Changes
              </Button>
            </Box>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <ModalClose />
          {actionError && <Alert color="danger">{actionError}</Alert>}
          <Typography level="h4" startDecorator={<WarningIcon color="var(--joy-palette-warning-500)" />}>
            Delete Tag
          </Typography>
          <Typography level="body-md">
            Are you sure you want to delete the tag <strong>"{selectedTag?.name}"</strong>?
          </Typography>
          {selectedTag && (selectedTag.usage_count || 0) > 0 && (
            <Box sx={{ p: 2, bgcolor: 'warning.softBg', borderRadius: 'sm' }}>
              <Typography level="body-sm">
                This tag is currently used on <strong>{selectedTag.usage_count} items</strong>.
                Deleting it will hide it from future selections, but it will remain on existing items.
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="plain" color="neutral" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button color="danger" onClick={handleDeleteTag} loading={isSubmitting}>
              Delete Tag
            </Button>
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default TagManagementPage;
